package ai.wagemix.web.components

import ai.wagemix.web.theme.WMBodySmallTextStyle
import ai.wagemix.web.theme.WMCardCornerVar
import ai.wagemix.web.theme.WMCardPaddingCompactVar
import ai.wagemix.web.theme.WMCardTextGapVar
import ai.wagemix.web.theme.WMCoachmarkGlowSpreadVar
import ai.wagemix.web.theme.WMCoachmarkPulseVar
import ai.wagemix.web.theme.WMCoachmarkRingReachVar
import ai.wagemix.web.theme.WMCoachmarkRingVar
import ai.wagemix.web.theme.WMFocusRingVar
import ai.wagemix.web.theme.WMInlineGapVar
import ai.wagemix.web.theme.WMLabelStrongTextStyle
import ai.wagemix.web.theme.WMOpacity
import ai.wagemix.web.theme.WMOverlineTextStyle
import ai.wagemix.web.theme.WMTightGapVar
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.Stable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.runtime.withFrameNanos
import com.fsryan.foundation.ui.tokens.FSMotionTokens
import com.fsryan.foundation.web.theme.FSMotionMediumVar
import com.fsryan.foundation.web.theme.fs
import com.varabyte.kobweb.compose.css.AnimationIterationCount
import com.varabyte.kobweb.compose.css.CSSLengthNumericValue
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.RowScope
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.animation
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.boxShadow
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexDirection
import com.varabyte.kobweb.compose.ui.modifiers.flexWrap
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.gridTemplateRows
import com.varabyte.kobweb.compose.ui.modifiers.justifyContent
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.minHeight
import com.varabyte.kobweb.compose.ui.modifiers.opacity
import com.varabyte.kobweb.compose.ui.modifiers.outline
import com.varabyte.kobweb.compose.ui.modifiers.outlineOffset
import com.varabyte.kobweb.compose.ui.modifiers.overflow
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.role
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.modifiers.translateY
import com.varabyte.kobweb.compose.ui.thenIf
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.ComponentKind
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.addVariant
import com.varabyte.kobweb.silk.style.animation.Keyframes
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette
import kotlinx.browser.window
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.css.AnimationFillMode
import org.jetbrains.compose.web.css.AnimationTimingFunction
import org.jetbrains.compose.web.css.Color
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.fr
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.times
import org.jetbrains.compose.web.dom.Div

/*
 * A guided tour: a callout per step (WMCoachmark) placed next to what the step points at, which is ringed with a
 * glow that pulses outward (WMCoachmarkTargetStyle). Steps never swap abruptly: rememberWMCoachmarkTransition holds
 * the step on screen while it fades out, and WMCoachmarkSlot opens and closes the room a callout takes, so the page
 * around it slides rather than jumps. Readers who prefer reduced motion get a still ring and no animation.
 */

/**
 * One beat of the ring: it swells away from what it points at and settles back, while a strong glow leaves the
 * border and fades as it spreads.
 */
val WMCoachmarkPulseKeyframes = Keyframes {
    val glow = colorMode.toPalette().fs.primary.toRgb()
    from {
        Modifier
            .outlineOffset(WMTightGapVar.value())
            .boxShadow(spreadRadius = 0.px, color = glow.copyf(alpha = WMOpacity.COACHMARK_GLOW))
    }
    50.percent { Modifier.outlineOffset(WMCoachmarkRingReachVar.value()) }
    to {
        Modifier
            .outlineOffset(WMTightGapVar.value())
            .boxShadow(spreadRadius = WMCoachmarkGlowSpreadVar.value(), color = glow.copyf(alpha = 0f))
    }
}

/** The ring of a step that is ending: it fades away with the callout. */
val WMCoachmarkRingOutKeyframes = Keyframes {
    from { Modifier.styleModifier { property("outline-color", colorMode.toPalette().fs.primary) } }
    to { Modifier.styleModifier { property("outline-color", Color.transparent) } }
}

/** A callout arriving: it fades in as it rises into place. */
val WMCoachmarkInKeyframes = Keyframes {
    from { Modifier.opacity(0).translateY(WMInlineGapVar.value()) }
    to { Modifier.opacity(1).translateY(0.px) }
}

/** A callout leaving: it fades out as it lifts away. */
val WMCoachmarkOutKeyframes = Keyframes {
    from { Modifier.opacity(1).translateY(0.px) }
    to { Modifier.opacity(0).translateY(WMInlineGapVar.value() * -1) }
}

/** The room a callout takes opening (the grid row grows from nothing to the callout's height). */
val WMCoachmarkOpenKeyframes = Keyframes {
    from { Modifier.gridTemplateRows { size(0.fr) } }
    to { Modifier.gridTemplateRows { size(1.fr) } }
}

/** The room a callout takes closing once its tour, or its part of the page, is done. */
val WMCoachmarkCloseKeyframes = Keyframes {
    from { Modifier.gridTemplateRows { size(1.fr) } }
    to { Modifier.gridTemplateRows { size(0.fr) } }
}

/** The callout of a guided tour: a surface card ringed in the primary colour so it reads as a step, not a notice. */
val WMCoachmarkStyle = CssStyle.base {
    val colors = colorMode.toPalette().fs
    Modifier
        .fillMaxWidth()
        .display(DisplayStyle.Flex)
        .flexDirection(FlexDirection.Column)
        .gap(WMCardTextGapVar.value())
        .padding(WMCardPaddingCompactVar.value())
        .borderRadius(WMCardCornerVar.value())
        .backgroundColor(colors.surface)
        .color(colors.onSurface)
        .border(WMFocusRingVar.value(), LineStyle.Solid, colors.primary)
}

/** The callout's buttons: right-aligned, wrapping under each other on a narrow column. */
val WMCoachmarkActionsStyle = CssStyle.base {
    Modifier
        .fillMaxWidth()
        .display(DisplayStyle.Flex)
        .flexWrap(FlexWrap.Wrap)
        .justifyContent(JustifyContent.FlexEnd)
        .gap(WMInlineGapVar.value())
}

sealed interface WMCoachmarkTargetKind: ComponentKind

/**
 * The control or region a tour step points at: ringed in the primary colour just outside its own border (following
 * its corners), with a glow that keeps pulsing outward from the ring to draw the eye.
 */
val WMCoachmarkTargetStyle = CssStyle<WMCoachmarkTargetKind> {
    base {
        Modifier
            .outline(WMCoachmarkRingVar.value(), LineStyle.Solid, colorMode.toPalette().fs.primary)
            .outlineOffset(WMTightGapVar.value())
            .animation(
                WMCoachmarkPulseKeyframes.toAnimation(
                    duration = WMCoachmarkPulseVar.value(),
                    timingFunction = AnimationTimingFunction.EaseInOut,
                    iterationCount = AnimationIterationCount.Infinite,
                )
            )
    }
    cssRule(WMReducedMotion) { Modifier.styleModifier { property("animation", "none") } }
}

/** The ring of a step that is ending: no more glow, and the ring fades with its callout. */
val WMCoachmarkLeavingTargetVariant = WMCoachmarkTargetStyle.addVariant {
    base {
        Modifier.animation(
            WMCoachmarkRingOutKeyframes.toAnimation(
                duration = FSMotionMediumVar.value(),
                timingFunction = AnimationTimingFunction.EaseIn,
                fillMode = AnimationFillMode.Forwards,
            )
        )
    }
}

sealed interface WMCoachmarkSlotKind: ComponentKind

/** The room a callout takes; it opens when a callout arrives in it. */
val WMCoachmarkSlotStyle = CssStyle<WMCoachmarkSlotKind> {
    base {
        Modifier
            .fillMaxWidth()
            .display(DisplayStyle.Grid)
            .gridTemplateRows { size(1.fr) }
            .animation(
                WMCoachmarkOpenKeyframes.toAnimation(
                    duration = FSMotionMediumVar.value(),
                    timingFunction = AnimationTimingFunction.EaseOut,
                )
            )
    }
    cssRule(WMReducedMotion) { Modifier.styleModifier { property("animation", "none") } }
}

/** The room of a callout whose tour is over, or whose next step is elsewhere on the page: it closes. */
val WMClosingCoachmarkSlotVariant = WMCoachmarkSlotStyle.addVariant {
    base {
        Modifier.animation(
            WMCoachmarkCloseKeyframes.toAnimation(
                duration = FSMotionMediumVar.value(),
                timingFunction = AnimationTimingFunction.EaseIn,
                fillMode = AnimationFillMode.Forwards,
            )
        )
    }
}

/** What clips the callout while its room opens or closes; a grid row can only shrink below content that allows it. */
val WMCoachmarkSlotClipStyle = CssStyle.base {
    Modifier.minHeight(0.px).overflow(Overflow.Hidden)
}

sealed interface WMCoachmarkMotionKind: ComponentKind

/** A callout arriving. */
val WMCoachmarkMotionStyle = CssStyle<WMCoachmarkMotionKind> {
    base {
        Modifier.animation(
            WMCoachmarkInKeyframes.toAnimation(
                duration = FSMotionMediumVar.value(),
                timingFunction = AnimationTimingFunction.EaseOut,
                fillMode = AnimationFillMode.Both,
            )
        )
    }
    cssRule(WMReducedMotion) { Modifier.styleModifier { property("animation", "none") } }
}

/** A callout leaving. */
val WMLeavingCoachmarkMotionVariant = WMCoachmarkMotionStyle.addVariant {
    base {
        Modifier.animation(
            WMCoachmarkOutKeyframes.toAnimation(
                duration = FSMotionMediumVar.value(),
                timingFunction = AnimationTimingFunction.EaseIn,
                fillMode = AnimationFillMode.Forwards,
            )
        )
    }
}

/**
 * Where a guided tour is on screen, which trails the step it was asked to show: when the step changes, the one on
 * screen first spends a moment [leaving] (its callout and ring fade out), and only then does [step] become the new
 * one. [next] is the step that follows, so a callout can tell whether the room it takes is still needed.
 */
@Stable
interface WMCoachmarkTransition<T: Any> {
    /** The step on screen, `null` when the tour shows nothing. */
    val step: T?
    /** The step on screen is fading out. */
    val leaving: Boolean
    /** The step that will be on screen once [step] has left; equal to [step] while it is not leaving. */
    val next: T?
}

private class WMCoachmarkTransitionState<T: Any>(initial: T?): WMCoachmarkTransition<T> {
    override var step: T? by mutableStateOf(initial)
    override var leaving: Boolean by mutableStateOf(false)
    override var next: T? by mutableStateOf(initial)
}

/**
 * Follows the tour step [target] with a fade between steps: a new step waits while the old one leaves, and the last
 * one leaves before the tour disappears. Readers who prefer reduced motion get each step at once.
 */
@Composable
fun <T: Any> rememberWMCoachmarkTransition(target: T?): WMCoachmarkTransition<T> {
    val transition = remember { WMCoachmarkTransitionState(target) }
    LaunchedEffect(target) {
        transition.next = target
        if (transition.step == target) {
            transition.leaving = false
            return@LaunchedEffect
        }
        if (transition.step != null && !prefersReducedMotion()) {
            transition.leaving = true
            // The fade is timed from the frame that shows it; a page that paints slowly would otherwise skip it.
            withFrameNanos { }
            delay(FSMotionTokens.defaultValue.medium)
        }
        transition.step = target
        transition.leaving = false
    }
    return transition
}

/**
 * The room for a tour's callout in one place on the page: it holds the steps [holds] accepts and renders each with
 * [content]. The room opens when a step arrives and closes when the tour moves elsewhere or ends, so what is below
 * it slides instead of jumping; a step changing in place fades out and the next one fades in. [spacing] is the gap
 * of the column the room sits in: the room takes it with it as it closes, so no empty gap is left behind.
 */
@Composable
fun <T: Any> WMCoachmarkSlot(
    transition: WMCoachmarkTransition<T>,
    holds: (T) -> Boolean,
    spacing: CSSLengthNumericValue,
    modifier: Modifier = Modifier,
    content: @Composable (T) -> Unit
) {
    val step = transition.step?.takeIf(holds) ?: return
    val closing = transition.leaving && transition.next?.let(holds) != true
    Div(
        WMCoachmarkSlotStyle.toModifier(if (closing) WMClosingCoachmarkSlotVariant else null)
            .margin(bottom = spacing * -1)
            .then(modifier)
            .toAttrs()
    ) {
        Div(WMCoachmarkSlotClipStyle.toModifier().toAttrs()) {
            key(step) {
                Div(
                    WMCoachmarkMotionStyle.toModifier(if (transition.leaving) WMLeavingCoachmarkMotionVariant else null)
                        .padding(bottom = spacing)
                        .thenIf(transition.leaving) { Modifier.attrsModifier { attr("inert", "") } }
                        .toAttrs()
                ) {
                    content(step)
                }
            }
        }
    }
}

/** The ring for what tour step [step] points at, while [transition] shows it; nothing otherwise. */
@Composable
fun <T: Any> Modifier.coachmarkTarget(transition: WMCoachmarkTransition<T>, step: T): Modifier {
    return thenIf(transition.step == step) {
        WMCoachmarkTargetStyle.toModifier(if (transition.leaving) WMCoachmarkLeavingTargetVariant else null)
    }
}

/**
 * One step of a guided tour, shown next to what it points at (ring that with [coachmarkTarget]): an optional
 * [progress] line ("Step 1 of 4"), a [title], a [message] and the step's [actions] (Skip, Next, Done). Once it has
 * arrived it scrolls into view if it is off screen, so a step further down a phone's single column is not missed,
 * and it is a `status` region, so a screen reader announces each step.
 */
@Composable
fun WMCoachmark(
    title: String,
    message: String,
    modifier: Modifier = Modifier,
    progress: String? = null,
    actions: @Composable RowScope.() -> Unit
) {
    val colors = ColorMode.current.toPalette().fs
    Div(
        WMCoachmarkStyle.toModifier().role("status").then(modifier).toAttrs {
            ref { element ->
                // After its room has opened, or "nearest" measures a callout that is still growing.
                val reduced = prefersReducedMotion()
                val wait = if (reduced) 0 else FSMotionTokens.defaultValue.medium.inWholeMilliseconds.toInt()
                val behavior = if (reduced) "auto" else "smooth"
                val handle = window.setTimeout(
                    { element.asDynamic().scrollIntoView(js("({ block: 'nearest', behavior: behavior })")) },
                    wait
                )
                onDispose { window.clearTimeout(handle) }
            }
        }
    ) {
        Column(modifier = Modifier.fillMaxWidth().gap(WMTightGapVar.value())) {
            progress?.let { SpanText(it, WMOverlineTextStyle.toModifier().color(colors.primary)) }
            SpanText(title, WMLabelStrongTextStyle.toModifier())
            SpanText(message, WMBodySmallTextStyle.toModifier().color(colors.onSurfaceVariant))
        }
        Row(modifier = WMCoachmarkActionsStyle.toModifier(), verticalAlignment = Alignment.CenterVertically) {
            actions()
        }
    }
}

private fun prefersReducedMotion(): Boolean = window.matchMedia("(prefers-reduced-motion: reduce)").matches
